"use client"

import { useState, useRef, useEffect } from "react"
import { Mic } from "lucide-react"
import { detectMicrophones } from "../utils/mediaDetection.js"
import { toast } from "react-toastify"

export function MicrophoneTest() {
    const [micState, setMicState] = useState({
        availableMicrophones: [],
        selectedMic: "",
        isTesting: false,
        micLevel: 0,
        isLoading: false
    })

    const audioContextRef = useRef(null)
    const analyserRef = useRef(null)
    const animationRef = useRef(null)
    const streamRef = useRef(null)
    const isTestingRef = useRef(false)
    const isMountedRef = useRef(true)

    const loadMicrophones = async () => {
        if (!isMountedRef.current) return

        try {
            setMicState(prev => ({ ...prev, isLoading: true }))

            if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) {
                throw new Error("Media devices not supported in this browser")
            }

            const microphones = await detectMicrophones()

            if (!isMountedRef.current) return

            if (microphones.length === 0) {
                toast.error("No microphones detected. Please check your device connections.")
                setMicState(prev => ({
                    ...prev,
                    availableMicrophones: [],
                    selectedMic: "",
                    isLoading: false
                }))
                return
            }

            const commsMic = microphones.find(mic => mic.deviceType === 'communications')

            setMicState(prev => ({
                ...prev,
                availableMicrophones: microphones,
                selectedMic: prev.selectedMic || (commsMic?.id || microphones[0]?.id || ""),
                isLoading: false
            }))
        } catch (error) {
            console.error("Error loading microphones:", error)
            if (!isMountedRef.current) return

            setMicState(prev => ({
                ...prev,
                isLoading: false,
                error: error.message
            }))
            toast.error(`Failed to load microphones: ${error.message}`)
        }
    }

    const startMicTest = async () => {
        if (!isMountedRef.current) return

        try {
            // Validate selected microphone
            if (!micState.selectedMic) {
                toast.error("Please select a microphone first")
                return
            }

            setMicState(prev => ({ ...prev, isTesting: true }))
            isTestingRef.current = true

            // Check for getUserMedia support
            if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
                throw new Error("getUserMedia not supported in this browser")
            }

            const constraints = {
                audio: micState.selectedMic
                    ? {
                        deviceId: { exact: micState.selectedMic },
                        echoCancellation: true,
                        noiseSuppression: true,
                        autoGainControl: true
                    }
                    : true
            }

            const mediaStream = await navigator.mediaDevices.getUserMedia(constraints)

            if (!isMountedRef.current || !isTestingRef.current) {
                mediaStream.getTracks().forEach(track => track.stop())
                return
            }

            streamRef.current = mediaStream

            // Check for AudioContext support
            const AudioContextClass = window.AudioContext || window.webkitAudioContext
            if (!AudioContextClass) {
                throw new Error("Web Audio API not supported in this browser")
            }

            const audioContext = new AudioContextClass()
            audioContextRef.current = audioContext

            if (audioContext.state === 'suspended') {
                await audioContext.resume()
            }

            const analyser = audioContext.createAnalyser()
            analyser.fftSize = 256

            const microphone = audioContext.createMediaStreamSource(mediaStream)
            microphone.connect(analyser)

            analyserRef.current = analyser

            const visualize = () => {
                if (!isTestingRef.current || !analyserRef.current || !isMountedRef.current) return

                try {
                    const bufferLength = analyserRef.current.frequencyBinCount
                    const dataArray = new Uint8Array(bufferLength)
                    analyserRef.current.getByteTimeDomainData(dataArray)

                    let sum = 0
                    for (let i = 0; i < bufferLength; i++) {
                        const v = dataArray[i] / 128.0
                        sum += Math.abs(v - 1.0)
                    }
                    const avg = sum / bufferLength
                    const scaledLevel = avg * 5

                    const level = Math.floor(scaledLevel * 20)
                    const clampedLevel = Math.min(Math.max(level, 0), 20)

                    if (isMountedRef.current) {
                        setMicState(prev => ({ ...prev, micLevel: clampedLevel }))
                        animationRef.current = requestAnimationFrame(visualize)
                    }
                } catch (error) {
                    console.error("Visualization error:", error)
                    toast.error("Error during microphone visualization")
                    stopMicTest()
                }
            }

            visualize()

        } catch (error) {
            console.error("Microphone test error:", error)

            if (!isMountedRef.current) return

            setMicState(prev => ({
                ...prev,
                isTesting: false,
                error: error.message
            }))
            isTestingRef.current = false

            let errorMessage = "Failed to start microphone test"
            if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
                errorMessage = "Microphone permission denied. Please allow microphone access."
            } else if (error.name === 'NotFoundError' || error.name === 'DeviceNotFoundError') {
                errorMessage = "Selected microphone not found. Please select a different microphone."
            } else if (error.name === 'NotSupportedError') {
                errorMessage = "Microphone not supported in this browser."
            } else if (error.name === 'OverconstrainedError') {
                errorMessage = "Unable to use selected microphone with current settings."
            } else {
                errorMessage = `Microphone error: ${error.message}`
            }

            toast.error(errorMessage)
        }
    }

    const stopMicTest = () => {
        if (!isMountedRef.current) return

        setMicState(prev => ({ ...prev, isTesting: false, micLevel: 0 }))
        isTestingRef.current = false

        try {
            if (animationRef.current) {
                cancelAnimationFrame(animationRef.current)
                animationRef.current = null
            }

            if (streamRef.current) {
                streamRef.current.getTracks().forEach(track => {
                    try {
                        track.stop()
                    } catch (error) {
                        console.warn("Error stopping track:", error)
                    }
                })
                streamRef.current = null
            }

            if (audioContextRef.current) {
                audioContextRef.current.close().catch(error => {
                    console.warn("Error closing audio context:", error)
                })
                audioContextRef.current = null
            }
        } catch (error) {
            console.error("Error stopping microphone test:", error)
            toast.error("Error stopping microphone test")
        }
    }

    const handleMicrophoneChange = (micId) => {
        if (micState.isTesting) {
            stopMicTest()
        }
        setMicState(prev => ({ ...prev, selectedMic: micId }))
    }

    useEffect(() => {
        isMountedRef.current = true
        loadMicrophones()

        return () => {
            isMountedRef.current = false
            isTestingRef.current = false
            stopMicTest()
        }
    }, [])

    useEffect(() => {
        const handleDeviceChange = () => {
            if (isMountedRef.current) {
                toast.info("Audio devices changed, refreshing microphone list...")
                loadMicrophones()
            }
        }

        try {
            navigator.mediaDevices?.addEventListener('devicechange', handleDeviceChange)
        } catch (error) {
            console.warn("Device change listener not supported:", error)
        }

        return () => {
            try {
                navigator.mediaDevices?.removeEventListener('devicechange', handleDeviceChange)
            } catch (error) {
                console.warn("Error removing device change listener:", error)
            }
        }
    }, [])

    const renderMicBars = () => {
        const bars = []
        for (let i = 0; i < 20; i++) {
            const isActive = i < (micState?.micLevel ?? 0)
            bars.push(
                <div
                    key={i}
                    className={`mic-bar ${isActive ? "active" : ""}`}
                    style={{ backgroundColor: isActive ? '#007bff' : '#e0e0e0' }}
                />
            )
        }
        return bars
    }

    return (
        <div className="device-group">
            <div className="device-label">Microphone</div>

            <div className="device-options">
                {micState.availableMicrophones?.map((mic) => (
                    <label key={mic?.id} className="radio-option">
                        <input
                            type="radio"
                            name="microphone"
                            value={mic?.id}
                            checked={micState?.selectedMic === mic?.id}
                            onChange={(e) => handleMicrophoneChange(e.target.value)}
                            className="radio-input"
                            disabled={micState.isLoading}
                        />
                        <span className="radio-label">{mic?.label}</span>
                    </label>
                ))}
            </div>

            <div className="mic-level-container">
                <Mic className="mic-icon" />
                <div className="mic-level-bars">{renderMicBars()}</div>
            </div>

            <button
                onClick={micState?.isTesting ? stopMicTest : startMicTest}
                disabled={micState.availableMicrophones.length === 0 || micState.isLoading || !micState.selectedMic}
                className={`test-button ${micState?.isTesting ? "testing" : ""} ${(micState.availableMicrophones.length === 0 || micState.isLoading || !micState.selectedMic) ? "disabled" : ""
                    }`}
            >
                {micState?.isTesting ? "Stop Test" : "Test Microphone"}
            </button>
        </div>
    )
}